import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { getLanguageCode } from 'chat-ui/src/utils/getLanguageCode';
import { getRespondentUiTranslation } from 'chat-ui/src/translations/respondentUiTranslations';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches React rendering errors (e.g. browser auto-translate mutating
 * the DOM) and shows a recovery screen instead of a white crash page.
 * Reports the error to Sentry automatically.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Resolve language at render time (no project context available since the
      // tree crashed — fall back to localStorage-persisted lang then
      // navigator.language).
      const t = getRespondentUiTranslation(getLanguageCode());
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-white">
          <div className="flex flex-col items-center gap-4 max-w-sm">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50">
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{t.errorBoundaryTitle}</h2>
            <p className="text-sm text-gray-500">{t.errorBoundaryMessage}</p>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-5 py-2.5 text-sm font-medium text-white bg-[#FF6F98] rounded-lg hover:bg-[#e5607f] transition-colors"
            >
              {t.errorBoundaryReloadButton}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
